export interface ChartDatum {
  label: string
  value: number
}

interface ChartProps {
  data: ChartDatum[]
  height?: number
  formatValue?: (value: number) => string
  colorClass?: string
}

const WIDTH = 640
const PAD_L = 44
const PAD_R = 12
const PAD_T = 12
const PAD_B = 26

function chartGeometry(data: ChartDatum[], height: number) {
  const plotW = WIDTH - PAD_L - PAD_R
  const plotH = height - PAD_T - PAD_B
  const max = Math.max(1, ...data.map((d) => d.value))
  const n = Math.max(1, data.length)
  const y = (v: number) => PAD_T + plotH - (v / max) * plotH
  const ticks = [0, max / 2, max]
  const labelEvery = Math.max(1, Math.ceil(n / 8))
  return { plotW, plotH, max, n, y, ticks, labelEvery }
}

function Axis({
  height,
  ticks,
  y,
  formatValue,
}: {
  height: number
  ticks: number[]
  y: (v: number) => number
  formatValue: (v: number) => string
}) {
  return (
    <>
      {ticks.map((tk) => (
        <g key={tk}>
          <line
            x1={PAD_L}
            x2={WIDTH - PAD_R}
            y1={y(tk)}
            y2={y(tk)}
            className="stroke-border"
            strokeWidth={1}
            strokeDasharray={tk === 0 ? "0" : "3 3"}
          />
          <text
            x={PAD_L - 6}
            y={y(tk) + 3}
            textAnchor="end"
            fontSize={10}
            className="fill-muted-foreground"
          >
            {formatValue(tk)}
          </text>
        </g>
      ))}
    </>
  )
}

export function BarChart({
  data,
  height = 200,
  formatValue = (v) => String(v),
  colorClass,
}: ChartProps) {
  if (data.length === 0) return null
  const { plotW, max, n, y, ticks, labelEvery } = chartGeometry(data, height)
  const slot = plotW / n
  const barW = Math.max(1, slot * 0.68)

  return (
    <svg viewBox={`0 0 ${WIDTH} ${height}`} className="w-full h-auto" role="img">
      <Axis height={height} ticks={ticks} y={y} formatValue={formatValue} />
      {data.map((d, i) => {
        const x = PAD_L + i * slot + (slot - barW) / 2
        const barH = (d.value / max) * (height - PAD_T - PAD_B) || 0
        return (
          <rect
            key={i}
            x={x}
            y={PAD_T + (height - PAD_T - PAD_B) - barH}
            width={barW}
            height={Math.max(barH, d.value > 0 ? 2 : 0)}
            rx={1.5}
            className={colorClass ?? "fill-primary"}
          >
            <title>{`${d.label}: ${formatValue(d.value)}`}</title>
          </rect>
        )
      })}
      {data.map((d, i) =>
        i % labelEvery === 0 ? (
          <text
            key={`l-${i}`}
            x={PAD_L + i * slot + slot / 2}
            y={height - 8}
            textAnchor="middle"
            fontSize={10}
            className="fill-muted-foreground"
          >
            {d.label}
          </text>
        ) : null,
      )}
    </svg>
  )
}

export function LineChart({ data, height = 200, formatValue = (v) => String(v) }: ChartProps) {
  if (data.length === 0) return null
  const { plotW, n, y, ticks, labelEvery } = chartGeometry(data, height)
  const step = n > 1 ? plotW / (n - 1) : 0
  const x = (i: number) => PAD_L + (n > 1 ? i * step : plotW / 2)
  const points = data.map((d, i) => `${x(i)},${y(d.value)}`).join(" ")
  const bottom = height - PAD_B
  const area = `${PAD_L},${bottom} ${points} ${x(n - 1)},${bottom}`
  const showDots = data.length <= 45

  return (
    <svg viewBox={`0 0 ${WIDTH} ${height}`} className="w-full h-auto" role="img">
      <Axis height={height} ticks={ticks} y={y} formatValue={formatValue} />
      <polygon points={area} className="fill-primary" fillOpacity={0.12} stroke="none" />
      <polyline
        points={points}
        fill="none"
        className="stroke-primary"
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {showDots &&
        data.map((d, i) => (
          <circle key={i} cx={x(i)} cy={y(d.value)} r={2.5} className="fill-primary">
            <title>{`${d.label}: ${formatValue(d.value)}`}</title>
          </circle>
        ))}
      {data.map((d, i) =>
        i % labelEvery === 0 ? (
          <text
            key={`l-${i}`}
            x={x(i)}
            y={height - 8}
            textAnchor="middle"
            fontSize={10}
            className="fill-muted-foreground"
          >
            {d.label}
          </text>
        ) : null,
      )}
    </svg>
  )
}
